import React from "react";
import { getProductDisplayName } from "../utils/productDisplayName";
import {
  formatCustomerPaymentAmount,
  formatCustomerPaymentDateTime,
  getCustomerOnlinePaymentSummary,
} from "../utils/customerOnlinePaymentStatus";
import styles from "./CustomerOnlinePaymentStatus.module.css";

const TONE_CLASS_NAMES = {
  success: styles.toneSuccess,
  warning: styles.toneWarning,
  danger: styles.toneDanger,
  refunded: styles.toneRefunded,
  partialRefund: styles.tonePartialRefund,
  neutral: styles.toneNeutral,
};

const CustomerOnlinePaymentStatus = ({ order }) => {
  const summary = getCustomerOnlinePaymentSummary(order);
  const toneClassName = TONE_CLASS_NAMES[summary.tone] || styles.toneNeutral;

  if (!summary.applies) return null;

  return (
    <section
      className={`${styles.paymentStatusCard} ${toneClassName}`}
    >
      <div className={styles.header}>
        <span className={styles.title}>Статус он-лайн оплати</span>
        <span className={`${styles.badge} ${toneClassName}`}>
          {summary.statusLabel}
        </span>
      </div>

      {summary.message && <p className={styles.message}>{summary.message}</p>}

      {summary.rows.length > 0 && (
        <dl className={styles.infoGrid}>
          {summary.rows.map((row) => (
            <div className={styles.infoItem} key={row.label}>
              <dt>{row.label}</dt>
              <dd className={row.tone ? styles[row.tone] : ""}>{row.value}</dd>
            </div>
          ))}
        </dl>
      )}

      {summary.additionalPayment?.url && (
        <div className={styles.actionBox}>
          <span>
            До сплати:{" "}
            <strong>
              {formatCustomerPaymentAmount(summary.additionalPayment.amount)}
            </strong>
          </span>
          <button
            type="button"
            className={styles.actionButton}
            onClick={() => window.location.assign(summary.additionalPayment.url)}
          >
            Оплатити доплату
          </button>
        </div>
      )}

      {summary.refundGroups.length > 0 && (
        <div className={styles.subsection}>
          <h4>Повернення коштів</h4>
          <div className={styles.refundList}>
            {summary.refundGroups.map((refund, index) => (
              <div className={styles.refundCard} key={`${refund.label}-${index}`}>
                <div className={styles.refundHeader}>
                  <span>{refund.label}</span>
                  <b className={styles.danger}>
                    {formatCustomerPaymentAmount(-refund.amount, {
                      signed: true,
                    })}
                  </b>
                </div>
                <div className={styles.refundMeta}>
                  <span
                    className={`${styles.refundStatus} ${
                      styles[refund.statusTone] || ""
                    }`}
                  >
                    {refund.statusLabel}
                  </span>
                  {refund.date && (
                    <span>{formatCustomerPaymentDateTime(refund.date)}</span>
                  )}
                </div>
                {refund.items.length > 0 && (
                  <ul className={styles.itemList}>
                    {refund.items.map((item, itemIndex) => (
                      <li
                        key={`${item.productCode || item.name}-${itemIndex}`}
                        className={styles.itemRow}
                      >
                        <span>
                          {item.productCode ? `Артикул: ${item.productCode} | ` : ""}
                          {getProductDisplayName(item)}
                        </span>
                        <b>{item.quantity} од.</b>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {summary.returnedItemsSummary.count > 0 && (
        <div className={styles.subsection}>
          <h4>Повернення товарів</h4>
          <div className={styles.returnSummary}>
            <span>
              Оформлено повернення товарів:{" "}
              <strong>{summary.returnedItemsSummary.count}</strong>
            </span>
            {summary.returnedItemsSummary.amount > 0 && (
              <span className={styles.danger}>
                {formatCustomerPaymentAmount(-summary.returnedItemsSummary.amount, {
                  signed: true,
                })}
              </span>
            )}
          </div>
          <ul className={styles.itemList}>
            {summary.returnedItemsSummary.items.slice(0, 4).map((item, index) => (
              <li key={`${item.productCode || item.name}-${index}`}>
                <span>
                  {item.productCode ? `Артикул: ${item.productCode} | ` : ""}
                  {getProductDisplayName(item)}
                </span>
                <b>{item.quantity} од.</b>
              </li>
            ))}
          </ul>
        </div>
      )}

      {summary.events.length > 0 && (
        <div className={styles.events}>
          {summary.events.map((event, index) => (
            <div
              className={`${styles.eventItem} ${
                event.tone ? styles[event.tone] : ""
              }`}
              key={`${event.text}-${index}`}
            >
              <span>{event.text}</span>
              {event.amount ? (
                <b>
                  {event.tone === "danger"
                    ? formatCustomerPaymentAmount(-event.amount, { signed: true })
                    : formatCustomerPaymentAmount(event.amount)}
                </b>
              ) : null}
              {event.date ? (
                <small>{formatCustomerPaymentDateTime(event.date)}</small>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default CustomerOnlinePaymentStatus;
